import React, { useContext, useState, useRef, useEffect } from 'react';
import { ThemeContext } from '../context/ThemeContext';
import { FaChevronLeft, FaChevronRight } from 'react-icons/fa';
import { themes, THEMES_PER_PAGE, getTotalPages, getThemesByPage } from '../utils/themes';

const ThemeMenu = ({ isVisible, onClose, buttonRef }) => {
  const { theme, colorTheme, userColorTheme, currentIdeaTheme, toggleTheme, changeColorTheme, useIdeaTheme, toggleUseIdeaTheme } = useContext(ThemeContext);
  const menuRef = useRef(null);
  const [currentPage, setCurrentPage] = useState(0);
  const totalPages = getTotalPages();
  
  // Position the menu below the theme button
  const [menuStyle, setMenuStyle] = useState({
    position: 'fixed',
    top: 0,
    left: 0,
    zIndex: 1000,
    opacity: 0,
    visibility: 'hidden',
  });

  useEffect(() => {
    if (isVisible && buttonRef.current) {
      const buttonRect = buttonRef.current.getBoundingClientRect();
      setMenuStyle({
        position: 'fixed',
        top: buttonRect.bottom + 8,
        left: buttonRect.left - 200 + (buttonRect.width / 2), // Center menu under button
        zIndex: 1000,
        opacity: 1,
        visibility: 'visible',
        transition: 'opacity 0.2s ease-in-out',
      });
    } else {
      setMenuStyle(prev => ({
        ...prev,
        opacity: 0,
        visibility: 'hidden',
      }));
    }
  }, [isVisible, buttonRef]);

  // Close menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (menuRef.current && !menuRef.current.contains(event.target) && 
          buttonRef.current && !buttonRef.current.contains(event.target)) {
        onClose();
      }
    };

    if (isVisible) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [isVisible, onClose, buttonRef]);

  const userThemeLabel = themes.find(t => t.name === userColorTheme)?.label || 'Unknown';
  const ideaThemeLabel = themes.find(t => t.name === currentIdeaTheme)?.label || 'Unknown';
  const displayedThemes = getThemesByPage(currentPage);
  const isThemeOverridden = useIdeaTheme && currentIdeaTheme;

  const goToPrevPage = () => {
    setCurrentPage((prev) => (prev === 0 ? totalPages - 1 : prev - 1));
  };

  const goToNextPage = () => {
    setCurrentPage((prev) => (prev === totalPages - 1 ? 0 : prev + 1));
  };

  if (!isVisible) return null;

  return (
    <div 
      ref={menuRef}
      style={menuStyle}
      className="bg-[var(--bg-secondary)] border border-[var(--border-color)] rounded-lg shadow-lg p-4 w-72"
    >
      {/* Dark/Light Mode Toggle */}
      <div className="flex items-center justify-between mb-4">
        <span className="text-sm font-medium text-[var(--text-primary)]">Dark Mode</span>
        <button
          onClick={toggleTheme}
          className={`relative inline-flex h-6 w-11 items-center rounded-full border border-[var(--text-primary)] transition-colors focus:outline-none focus:ring-2 focus:ring-[var(--primary)] focus:ring-offset-2 ${
            theme === 'dark' ? 'bg-[var(--primary)]' : 'bg-[var(--bg-primary)]'
          }`}
        >
          <span
            className={`inline-block h-4 w-4 transform rounded-full bg-[var(--text-primary)] transition-transform ${
              theme === 'dark' ? 'translate-x-6' : 'translate-x-1'
            }`}
          />
        </button>
      </div>

      {/* Use Idea Theme Toggle */}
      <div className="flex items-center justify-between mb-4">
        <span className="text-sm font-medium text-[var(--text-primary)]">Use Idea Theme</span>
        <button
          onClick={toggleUseIdeaTheme}
          className={`relative inline-flex h-6 w-11 items-center rounded-full border border-[var(--text-primary)] transition-colors focus:outline-none focus:ring-2 focus:ring-[var(--primary)] focus:ring-offset-2 ${
            useIdeaTheme ? 'bg-[var(--primary)]' : 'bg-[var(--bg-primary)]'
          }`}
        >
          <span
            className={`inline-block h-4 w-4 transform rounded-full bg-[var(--text-primary)] transition-transform ${
              useIdeaTheme ? 'translate-x-6' : 'translate-x-1'
            }`}
          />
        </button>
      </div>

      {/* Color Theme Selection */}
      <div className="border-t border-[var(--border-color)] pt-3">
        {/* Override Indicator */}
        {isThemeOverridden && (
          <div className="mb-3 p-2 rounded-lg bg-[var(--primary)] bg-opacity-20 border border-[var(--primary)] border-opacity-30">
            <p className="text-xs text-[var(--text-primary)] font-medium">
              Using Idea Theme: {ideaThemeLabel}
            </p>
          </div>
        )}
        
        <div className="flex items-center justify-between mb-3">
          <h4 className="text-xs font-medium text-[var(--text-secondary)]">
            {isThemeOverridden ? `Your Theme (${userThemeLabel})` : `Color Theme (${userThemeLabel})`}
          </h4>
          <span className="text-xs text-[var(--text-secondary)]">
            {currentPage + 1} / {totalPages}
          </span>
        </div>
        
        <div className="flex items-center gap-2">
          {/* Previous Button */}
          <button
            onClick={goToPrevPage}
            className="flex-shrink-0 p-2 rounded-lg bg-[var(--bg-secondary)] hover:bg-[var(--bg-hover)] text-[var(--text-primary)] transition-colors"
            title="Previous page"
          >
            <FaChevronLeft className="w-3 h-3" />
          </button>

          {/* Theme Grid */}
          <div className="grid grid-cols-5 gap-2 flex-1">
            {displayedThemes.map((t) => {
              const colors = theme === 'dark' ? t.dark : t.light;
              return (
                <button
                  key={t.name}
                  onClick={() => {
                    changeColorTheme(t.name);
                    // Don't close the menu when selecting a theme
                  }}
                  className={`h-8 w-8 rounded-lg flex items-center justify-center transition-all hover:scale-110 ${
                    userColorTheme === t.name 
                      ? 'ring-2 ring-offset-2 ring-[var(--primary)]' 
                      : 'opacity-70 hover:opacity-100'
                  }`}
                  style={{
                    background: `linear-gradient(135deg, ${colors.primary}, ${colors.secondary})`
                  }}
                  title={t.label}
                >
                  {userColorTheme === t.name && (
                    <span className="text-white text-xs font-bold drop-shadow-md">
                      ✓
                    </span>
                  )}
                </button>
              );
            })}
          </div>

          {/* Next Button */}
          <button
            onClick={goToNextPage}
            className="flex-shrink-0 p-2 rounded-lg bg-[var(--bg-secondary)] hover:bg-[var(--bg-hover)] text-[var(--text-primary)] transition-colors"
            title="Next page"
          >
            <FaChevronRight className="w-3 h-3" />
          </button>
        </div>
      </div>
    </div>
  );
};

export default ThemeMenu;